// Web card-add sheet (CPS-26589 Adyen coexistence; CPS-26590 Task 7 wallets).
//
// Web counterpart to AdyenCardAddSheet.tsx (native). Same prop contract
// (AdyenCardAddSheetProps), same BFF wiring (onSubmit -> adyenCreatePayment,
// onAdditionalDetails -> adyenSubmitPaymentDetails), but driven by Adyen's Web
// Components (adyen-web v6, Advanced flow) mounted into a DOM host instead of the
// native `@adyen/react-native` SDK. `componentKind` (from useAdyenCardAdd) picks
// WHICH component mounts into that same host: 'card' -> Card, 'applepay' ->
// ApplePay, 'googlepay' -> GooglePay. There is no separate wallet-only render
// path — same host, same effect, same status-driven switch as the card flow.
//
// PRESENTATIONAL: the parent (AdyenCardAddContainer, via useAdyenCardAdd) owns
// all state and passes its output as props. This renders two things keyed on
// `status`:
//   - the adyen-web component, mounted into a DOM node, for the entry states
//     (methods/ready) — and kept mounted while submitting/action are in flight so
//     `element.handleAction` can drive inline 3DS2;
//   - in-tree RN UX (spinner / messages / retry) for the post-dismissal states
//     (activating/timedOut/refused/error), which render under react-native-web.
//
// `@adyen/adyen-web` is pulled in via a DYNAMIC import inside an effect, so it
// lands in a lazy async chunk and non-Adyen users never download it. Do NOT
// convert these to static imports.
//
// API grounded in the installed types (node_modules/@adyen/adyen-web v6.39.0,
// dist/es/index.d.ts) AND the runtime source (dist/es/components/**/*.js,
// inspected directly — the .d.ts alone doesn't show reject/resolve semantics):
//   - AdyenCheckout(props: CoreConfiguration): Promise<Core>
//   - CoreConfiguration: { clientKey?, environment?, paymentMethodsResponse?,
//       amount?: PaymentAmount, onSubmit?(state, component, actions),
//       onAdditionalDetails?(state, component, actions) }. `amount` set here
//     flows down to EVERY mounted element's props automatically (Core.
//     getCorePropsForComponent()) — it is a Core-level, not per-element, config.
//   - SubmitData = { data; isValid }, AdditionalDetailsData = { data } — so the
//     payload we forward to the BFF is `state.data`.
//   - `Card`/`ApplePay`/`GooglePay` are named exports (`CardElement as Card`,
//     `ApplePayElement as ApplePay`, `GooglePay`), all `extends UIElement`;
//     construct with `new Ctor(checkout, props?)`, then `.mount(domNode)` /
//     `.unmount()`. Merchant/configuration fields (Apple Pay's
//     `configuration.{merchantId,merchantName}`, Google Pay's
//     `configuration.merchantId`) are auto-populated from the matching
//     /paymentMethods entry — same as Card, no extra props needed to construct.
//   - `isAvailable(): Promise<void>` is a UIElement member. The base
//     (UIElement.js) implementation resolves immediately — a no-op for Card.
//     ApplePayElement overrides it: checks `location.protocol === 'https:'`
//     then `ApplePaySession.canMakePayments()`, rejecting an AdyenCheckoutError
//     otherwise. GooglePay overrides it: calls its own isReadyToPay() and
//     rejects unless `{result: true, paymentMethodPresent: true}`. Both are
//     real, live availability checks (unlike the native SDK, which has no web
//     equivalent) — see mountAdyenWebComponent below, which gates mount on this.
//   - handleAction lives on the mounted UIElement, NOT on Core — so a
//     3DS/redirect action is driven by `element.handleAction(action)`.
//
// BFF mapping vs. the native sheet: here the web `onSubmit`/`onAdditionalDetails`
// receive `{ data }`, so we forward `state.data`; the native SDK hands the payload
// object directly. Both feed the same prop callbacks the BFF expects.

import { BaseText, Button, Meta, Spinner } from 'aimo-ui';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityInfo } from 'react-native';
import { StyledWrapper } from 'styles/ContainerStyles';

// Static on purpose (unlike the SDK imports below): a failed lazy CSS chunk
// would reject the init Promise.all and kill the card form over styling. Tiny,
// and order-independent — its selectors outrank adyen.css by specificity.
import './adyenCardBrand.css';

import {
  APPLE_PAY_MERCHANT_ID,
  APPLE_PAY_MERCHANT_NAME,
} from 'config/applePay';
import type { CountryCode } from 'types/generatedSchemaTypes';
import { getCurrencyLocales } from 'utils/commonUtils';

import type {
  AdyenCardAddSheetProps,
  AdyenComponentKind,
  AdyenPaymentResult,
  AdyenStateData,
} from './adyenCardAddSheet.types';

// Default refusal category when the BFF didn't categorize the refusal.
const DEFAULT_REFUSAL_CATEGORY = 'USE_OTHER_CARD';

// The adyen-web UIElement surface this file touches, structurally typed at the
// SDK boundary (mirrors the existing `paymentMethods as never` /
// `environment as 'test' | 'live'` casts below — adyen-web's real types are
// awkward to thread through a dynamic import, so the boundary is cast once
// here rather than fighting them throughout).
type AdyenWebUIElement = {
  mount: (el: HTMLElement) => unknown;
  unmount: () => unknown;
  handleAction: (action: unknown) => unknown;
  isAvailable: () => Promise<void>;
};

// The three UIElement constructors this sheet ever mounts, keyed by name so
// the componentKind -> constructor mapping (below) reads directly off it.
type AdyenWebElementCtors = {
  Card: new (
    checkout: unknown,
    props?: Record<string, unknown>
  ) => AdyenWebUIElement;
  ApplePay: new (
    checkout: unknown,
    props?: Record<string, unknown>
  ) => AdyenWebUIElement;
  GooglePay: new (
    checkout: unknown,
    props?: Record<string, unknown>
  ) => AdyenWebUIElement;
};

// componentKind -> the AdyenWebElementCtors key it selects. A lookup table
// instead of a nested ternary (SonarQube S3358) — same mapping, no behavior
// change.
const ELEMENT_CTOR_KEY_BY_COMPONENT_KIND: Record<
  AdyenComponentKind,
  keyof AdyenWebElementCtors
> = {
  applepay: 'ApplePay',
  googlepay: 'GooglePay',
  card: 'Card',
};

// adyen-web reports a shopper-dismissed wallet sheet through onError as an
// AdyenCheckoutError named CANCEL. Surfacing it as the CANCELLED result native
// produces lets the host close instead of showing a terminal error.
export const isAdyenWebCancelError = (error: unknown): boolean =>
  (error as { name?: unknown } | null)?.name === 'CANCEL';

// Stored-wallet tokenization has no real purchase amount. adyen-web's Card
// needs none (CSE-only tokenization, no amount ever read). ApplePay/GooglePay
// DO render a real PassKit/Google Pay sheet, which needs a total to show — the
// BFF's /paymentMethods bootstrap carries no amount field to thread through
// (AdyenPaymentMethodsResult is {clientKey, environment, paymentMethods,
// checkoutAttemptId} only — types/generatedSchemaTypes.ts), so there is no
// server-driven value to use instead. This mirrors the SAME zero-value
// tokenization convention the native sheet already established for Apple/
// Google Pay (config/applePay.ts + AdyenCardAddSheet.tsx's `config` builder):
// a zero-value amount in the shopper's market currency, added ONLY for the
// wallet componentKinds so the plain-card Core config is untouched (no `amount`
// key at all, same as before this task).
export const walletAmountFor = (
  componentKind: AdyenComponentKind,
  countryCode: string
): { currency: string; value: number } | undefined =>
  componentKind === 'card'
    ? undefined
    : {
        currency: getCurrencyLocales(countryCode as CountryCode).currency,
        value: 0,
      };

// Constructs the componentKind-appropriate adyen-web element and mounts it,
// gated on isAvailable() (see the file header for what each Ctor's
// isAvailable() actually checks). isAvailable() rejecting (a real, live
// "this wallet isn't usable here" signal — no ApplePaySession, GooglePay
// isReadyToPay() says no, insecure context, …) makes this function reject
// too, WITHOUT mounting: the caller's existing init try/catch (below) routes
// that rejection to onError exactly like any other init/mount failure, which
// the parent (useAdyenCardAdd.onError) maps to the sheet's terminal `error`
// state — never a dead, half-mounted host.
//
// Extracted out of the mount effect so this gate is unit-testable without a
// real DOM host: this jest preset has no `document` (testEnvironment: node —
// see AdyenCardAddSheet.web.test.tsx's header note), so a literal `<div ref>`
// never resolves to a real node under `render()` and the effect always
// no-ops before reaching any of this. Same rationale as
// screens/payment/adyenRedirectResume.ts's extraction out of its screen.
// Thrown when the mount effect is torn down mid-isAvailable so the caller can
// skip onError (abort is not a user-facing failure).
export class MountAbortedError extends Error {
  constructor() {
    super('Adyen web component mount aborted');
    this.name = 'MountAbortedError';
  }
}

export const isMountAbortedError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'MountAbortedError';

// Clear any orphaned Adyen DOM from a prior raced mount. Best-effort: test
// doubles may not implement replaceChildren/innerHTML.
export const clearAdyenWebHost = (host: HTMLElement): void => {
  try {
    if (typeof host.replaceChildren === 'function') {
      host.replaceChildren();
      return;
    }
    host.innerHTML = '';
  } catch {
    // Host may be a test double without DOM APIs.
  }
};

export const mountAdyenWebComponent = async (
  ctors: AdyenWebElementCtors,
  checkout: unknown,
  componentKind: AdyenComponentKind,
  host: HTMLElement,
  // Element-level props (adyen-web's `new Element(checkout, props)` second
  // argument), selected per kind by elementPropsFor; omitted entirely for the
  // card kind so its construction stays byte-for-byte unchanged.
  elementProps?: Record<string, unknown>,
  // When aborted after isAvailable(), skip mount so a remount cannot stack a
  // second Google Pay / Apple Pay button in the same host.
  signal?: AbortSignal
): Promise<AdyenWebUIElement> => {
  const ElementCtor = ctors[ELEMENT_CTOR_KEY_BY_COMPONENT_KIND[componentKind]];
  const element = elementProps
    ? new ElementCtor(checkout, elementProps)
    : new ElementCtor(checkout);
  await element.isAvailable();
  if (signal?.aborted) {
    throw new MountAbortedError();
  }
  clearAdyenWebHost(host);
  element.mount(host);
  return element;
};

// adyen-web v6 awaits `actions.resolve/reject` from onSubmit/onAdditionalDetails
// (UIElement.submitUsingAdvancedFlow); wallet elements keep their native sheet
// open until then, so Google Pay spins into "request timed out" without it.
// Resolving with {resultCode, action} makes the SDK run any 3DS/redirect action
// itself, so nothing here may call handleAction on top of it.
//
// Failed resultCodes (Refused/Error/Cancelled) are resolved too, NOT rejected:
// the SDK classifies them itself (verifyPaymentDidNotFail) and drives the
// wallet failure UI — Google Pay resolves its popup with transactionState
// ERROR, Apple Pay completes the sheet with STATUS_FAILURE. Rejection is only
// for the BFF round-trip itself failing; the wallet elements read an optional
// { error: { googlePayError / applePayError } } off the rejection reason, so
// the caught error is passed through. No wallet message is synthesized from it:
// BFF/Apollo messages are developer-facing and unlocalized, while the SDK's
// no-error fallback is already a localized, shopper-safe generic failure.
type AdvancedFlowActions = {
  resolve: (response: { resultCode: string; action?: unknown }) => void;
  reject: (error?: unknown) => void;
};

export const buildAdvancedFlowHandlers = (
  callbacks: () => {
    onSubmit: (state: AdyenStateData) => Promise<AdyenPaymentResult>;
    onAdditionalDetails: (state: AdyenStateData) => Promise<AdyenPaymentResult>;
    onResult: (result: AdyenPaymentResult) => void;
    onError: (error: unknown) => void;
  }
): {
  handleSubmit: (
    state: { data: unknown },
    element: unknown,
    actions: AdvancedFlowActions
  ) => void;
  handleAdditionalDetails: (
    state: { data: unknown },
    element: unknown,
    actions: AdvancedFlowActions
  ) => void;
} => {
  const settle = (
    round: Promise<AdyenPaymentResult>,
    actions: AdvancedFlowActions
  ): void => {
    round
      .then((result) => {
        actions.resolve(
          result.action
            ? { resultCode: result.resultCode, action: result.action }
            : { resultCode: result.resultCode }
        );
        if (!result.action) {
          callbacks().onResult(result);
        }
      })
      .catch((error: unknown) => {
        actions.reject(error);
        callbacks().onError(error);
      });
  };
  return {
    handleSubmit: (state, _element, actions) =>
      settle(callbacks().onSubmit(state.data as AdyenStateData), actions),
    handleAdditionalDetails: (state, _element, actions) =>
      settle(
        callbacks().onAdditionalDetails(state.data as AdyenStateData),
        actions
      ),
  };
};

// Element props for the applepay kind — our OWN Apple Pay certificate is in
// play (the merchant.com.adyen.* id lives in our Apple Developer account), so
// adyen-web's default Adyen-managed onValidateMerchant does not apply: Safari's
// validationURL must round-trip through the caller-provided handler (BFF →
// payment service → Apple over merchant-identity mTLS), and the resulting
// merchant session is handed back to the SDK's resolve for
// completeMerchantValidation. The merchant identity mirrors the native sheet's
// single source, config/applePay.ts. A missing handler rejects immediately so
// the Apple Pay sheet errors instead of hanging on a validation that can never
// complete.
export const buildApplePayMerchantValidationProps = (
  validate: ((validationUrl: string) => Promise<unknown>) | undefined
): {
  configuration: { merchantId: string; merchantName: string };
  onValidateMerchant: (
    resolve: (session: unknown) => void,
    reject: (error?: unknown) => void,
    validationUrl: string
  ) => void;
} => ({
  configuration: {
    merchantId: APPLE_PAY_MERCHANT_ID,
    merchantName: APPLE_PAY_MERCHANT_NAME,
  },
  onValidateMerchant: (resolve, reject, validationUrl) => {
    if (!validate) {
      reject(
        new Error(
          '[AdyenCardAddSheet.web] no onValidateMerchant handler wired for Apple Pay'
        )
      );
      return;
    }
    validate(validationUrl).then(resolve).catch(reject);
  },
});

// Element props per componentKind (adyen-web's `new Element(checkout, props)`
// second argument), extracted out of the mount effect for the same DOM-free
// testability reason as walletAmountFor: applepay carries merchant validation
// + merchant identity, googlepay carries the card-on-file price status, card
// needs none (CSE-only tokenization, no element-level props).
export const elementPropsFor = (
  componentKind: AdyenComponentKind,
  onValidateMerchant: (validationUrl: string) => Promise<unknown>
): Record<string, unknown> | undefined => {
  switch (componentKind) {
    case 'applepay':
      // 'plain' (logo only): adyen-web defaults to 'buy', but nothing is bought
      // here and Apple offers no "add card" label.
      return {
        ...buildApplePayMerchantValidationProps(onValidateMerchant),
        buttonType: 'plain',
      };
    case 'googlepay':
      // adyen-web forwards these into PaymentDataRequest.transactionInfo,
      // which accepts only ESTIMATED or FINAL; NOT_CURRENTLY_KNOWN makes Google
      // reject the sheet with OR_BIBED_06. ESTIMATED because the zero-auth is
      // a verification and the real fee is charged later as an MIT.
      // 'plain' (logo only): adyen-web defaults to 'buy', but nothing is bought
      // here. Google's brand review rejects a label that mismatches the action.
      return { totalPriceStatus: 'ESTIMATED', buttonType: 'plain' };
    default:
      return undefined;
  }
};

const AdyenCardAddSheet = (
  props: AdyenCardAddSheetProps
): React.JSX.Element | null => {
  const {
    clientKey,
    environment,
    countryCode,
    paymentMethods,
    onSubmit,
    onAdditionalDetails,
    onResult,
    onError,
    status,
    refusalReasonCategory,
    // `returnUrl` is part of the shared AdyenCardAddSheetProps (the native sheet
    // passes it to the SDK's config.returnUrl as a custom URL scheme). The WEB
    // sheet intentionally does NOT consume it: in adyen-web v6 the redirect
    // returnUrl is sent server-side via adyenCreatePayment, not via Core config
    // (see the AdyenCheckout call below). So it's deliberately not destructured.
    onRetry,
    // Defaults to 'card' — the existing plain-card sheets (which predate this
    // prop and never pass it) must stay behaviorally unchanged. 'applepay'/
    // 'googlepay' (CPS-26590 Task 7) mount the matching adyen-web wallet
    // component into the same host instead of Card — see
    // mountAdyenWebComponent above.
    componentKind = 'card',
    onValidateMerchant,
  } = props;

  const { t } = useTranslation();
  const payButtonLabel = t('payment.adyen.addCard');
  const hostRef = useRef<HTMLDivElement | null>(null);
  // Always-unmountable handle for the live adyen-web element. Cleanup must not
  // rely only on a closure-local `card` that is still null while isAvailable()
  // is in flight — that race stacked duplicate Google Pay buttons in the host.
  const mountedElementRef = useRef<AdyenWebUIElement | null>(null);

  // Callbacks are read through a ref so the effect can stay keyed to the payment
  // config identity (no remount on every parent render) without going stale: the
  // ref always points at the latest closures the parent passed.
  const cbRef = useRef({
    onSubmit,
    onAdditionalDetails,
    onResult,
    onError,
    onValidateMerchant,
  });
  cbRef.current = {
    onSubmit,
    onAdditionalDetails,
    onResult,
    onError,
    onValidateMerchant,
  };

  // Announce working/terminal states to screen readers once, on entry. Keyed on
  // `status` so it fires once per transition, not on every render. The
  // reader would otherwise only pick a state's text up on focus — which never
  // happens for the states whose own card form has been torn down. Mirrors the
  // native sheet.
  useEffect(() => {
    switch (status) {
      case 'activating':
        AccessibilityInfo.announceForAccessibility(
          t('payment.adyen.activating')
        );
        break;
      case 'refused':
        AccessibilityInfo.announceForAccessibility(
          t(
            `payment.adyen.refused.${refusalReasonCategory ?? DEFAULT_REFUSAL_CATEGORY}`
          )
        );
        break;
      case 'error':
        AccessibilityInfo.announceForAccessibility(t('payment.adyen.error'));
        break;
      case 'timedOut':
        AccessibilityInfo.announceForAccessibility(
          t('payment.adyen.willAppearShortly')
        );
        break;
    }
  }, [status, t, refusalReasonCategory]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }

    const abortController = new AbortController();
    // The mounted adyen-web UIElement (Card/ApplePay/GooglePay, per
    // componentKind). Kept for handleAction + unmount.
    let card: AdyenWebUIElement | null = null;

    const teardownMountedElement = (): void => {
      abortController.abort();
      const live = mountedElementRef.current ?? card;
      mountedElementRef.current = null;
      card = null;
      try {
        live?.unmount();
      } catch {
        // Unmounting a never-mounted / already-torn-down component is a no-op.
      }
      clearAdyenWebHost(host);
    };

    // Drop any orphaned DOM left by a previous raced mount before starting.
    clearAdyenWebHost(host);

    const init = async (): Promise<void> => {
      try {
        // Dynamic imports — these resolve into a lazy chunk, not the entry bundle.
        const [AdyenWeb] = await Promise.all([
          import('@adyen/adyen-web'),
          import('@adyen/adyen-web/styles/adyen.css'),
        ]);

        if (abortController.signal.aborted) {
          return;
        }

        // Forward SDK-surfaced errors to the caller's onError. BFF round-trip
        // failures go through buildAdvancedFlowHandlers' reject path instead.
        const forwardError = (error: unknown): void => {
          if (isAdyenWebCancelError(error)) {
            cbRef.current.onResult({ resultCode: 'CANCELLED' });
            return;
          }
          cbRef.current.onError(error);
        };

        // Redirect resume needs no work here: AdyenCardAddContainer stashes
        // {cardId, transactionId, checkoutAttemptId} in sessionStorage at
        // bootstrap, before any RedirectShopper navigation can fire.
        const { handleSubmit, handleAdditionalDetails } =
          buildAdvancedFlowHandlers(() => cbRef.current);

        // Core-level amount (see walletAmountFor above): undefined for 'card'
        // (spread away entirely below, so the plain-card Core config is
        // byte-for-byte the same as before this task), a zero-value amount for
        // the wallet kinds. Flows down to every mounted element's props
        // automatically.
        const amount = walletAmountFor(componentKind, countryCode);

        const checkout = await AdyenWeb.AdyenCheckout({
          clientKey,
          // CoreConfiguration types `environment` as a union; the prop is a plain
          // string from the BFF, so cast at the boundary.
          environment: environment as 'test' | 'live',
          // adyen-web v6 requires countryCode at Core init (it throws
          // IMPLEMENTATION_ERROR without it). Sourced from the gated app country.
          countryCode,
          paymentMethodsResponse: paymentMethods as never,
          ...(amount ? { amount } : {}),
          // A card add tokenizes; adyen-web would still label the Card button
          // "Pay". No `locale` is set, so adyen-web runs its en-US table and
          // the override has to target that table.
          translations: { 'en-US': { payButton: payButtonLabel } },
          // adyen-web v6 does NOT accept `origin` or `returnUrl` as Core config:
          // `origin` is auto-detected from the page, and the redirect `returnUrl`
          // is sent server-side via the BFF's adyenCreatePayment input. Passing
          // `origin` here triggers an "is not a valid AdyenCheckout property"
          // warning, so it is intentionally omitted.
          onSubmit: handleSubmit,
          onAdditionalDetails: handleAdditionalDetails,
          onError: forwardError,
        });

        if (abortController.signal.aborted) {
          return;
        }

        // Construct + isAvailable()-gate + mount the componentKind-appropriate
        // element. An unavailable wallet (isAvailable() rejects — see the file
        // header) throws here, is caught below, and routes to onError exactly
        // like any other init failure — never a dead host.
        //
        // `AdyenWeb` (the dynamic import's real, precise types) is cast to the
        // narrower AdyenWebElementCtors boundary shape at this one call site —
        // same pattern as `paymentMethods as never` / `environment as 'test' |
        // 'live'` above; adyen-web's real constructor signatures are awkward to
        // thread through a dynamically-imported module type.
        // Element props per kind (see elementPropsFor): applepay merchant
        // validation reads its handler through cbRef so it never goes stale,
        // googlepay carries the card-on-file price status, card passes
        // nothing and stays byte-for-byte unchanged.
        const elementProps = elementPropsFor(componentKind, (validationUrl) => {
          const validate = cbRef.current.onValidateMerchant;
          return validate
            ? validate(validationUrl)
            : Promise.reject(
                new Error(
                  '[AdyenCardAddSheet.web] no onValidateMerchant handler wired for Apple Pay'
                )
              );
        });

        card = await mountAdyenWebComponent(
          AdyenWeb as unknown as AdyenWebElementCtors,
          checkout,
          componentKind,
          host,
          elementProps,
          abortController.signal
        );
        if (abortController.signal.aborted) {
          try {
            card.unmount();
          } catch {
            // no-op
          }
          clearAdyenWebHost(host);
          return;
        }
        mountedElementRef.current = card;
      } catch (error) {
        if (abortController.signal.aborted || isMountAbortedError(error)) {
          return;
        }
        // Surface the real SDK init/mount failure — without this the error is
        // swallowed into the hook's `error` state with no console trace.
        console.error('[AdyenCardAddSheet.web] SDK init/mount failed', error);
        cbRef.current.onError(error);
      }
    };

    init().catch((error: unknown) => {
      if (abortController.signal.aborted || isMountAbortedError(error)) {
        return;
      }
      console.error('[AdyenCardAddSheet.web] init promise rejected', error);
      cbRef.current.onError(error);
    });

    return () => {
      teardownMountedElement();
    };
    // Re-init when the payment config identity OR componentKind changes.
    // Callbacks are read through cbRef (a stable ref), so they're correctly NOT
    // deps — the effect stays keyed to config identity and never sees a stale
    // closure. returnUrl is no longer a dep: the web sheet doesn't consume it
    // (v6 carries the redirect returnUrl server-side), so it's not part of this
    // effect's config identity.
  }, [
    clientKey,
    environment,
    countryCode,
    paymentMethods,
    componentKind,
    payButtonLabel,
  ]);

  // The DOM host the adyen-web element mounts into (Card/ApplePay/GooglePay,
  // per componentKind). Always the first child under the same StyledWrapper for
  // entry + submitting/action so status flips do not remount the host node and
  // re-trigger the async mount race. Kept mounted for submitting/action so the
  // in-flight element can still drive inline 3DS2 via handleAction. Web
  // cancellation is handled by the PARENT (modal close), so — unlike native —
  // there is no cancel detection here.
  const cardHost = <div ref={hostRef} className="aimo-adyen" />;

  // Post-dismissal states render in the RN(-web) tree. cancelled/done show
  // nothing.
  switch (status) {
    case 'activating':
      return (
        <StyledWrapper alignItems="center" mb={0}>
          <Spinner />
          <BaseText center testID="adyen-sheet-activating">
            {t('payment.adyen.activating')}
          </BaseText>
          <Meta center testID="adyen-sheet-activating-hint">
            {t('payment.adyen.activatingHint')}
          </Meta>
        </StyledWrapper>
      );
    case 'timedOut':
      return (
        <StyledWrapper mb={0}>
          <BaseText center testID="adyen-sheet-timedout">
            {t('payment.adyen.willAppearShortly')}
          </BaseText>
        </StyledWrapper>
      );
    case 'refused':
      return (
        <StyledWrapper mb={0}>
          <StyledWrapper mb={25}>
            <BaseText center testID="adyen-sheet-refused">
              {t(
                `payment.adyen.refused.${refusalReasonCategory ?? DEFAULT_REFUSAL_CATEGORY}`
              )}
            </BaseText>
          </StyledWrapper>
          <Button
            testID="adyen-sheet-retry"
            size="l"
            text={t('button.tryAgain')}
            onPress={onRetry}
          />
        </StyledWrapper>
      );
    case 'error':
      return (
        <StyledWrapper mb={0}>
          <StyledWrapper mb={25}>
            <BaseText center testID="adyen-sheet-error">
              {t('payment.adyen.error')}
            </BaseText>
          </StyledWrapper>
          <Button
            testID="adyen-sheet-retry"
            size="l"
            text={t('button.tryAgain')}
            onPress={onRetry}
          />
        </StyledWrapper>
      );
    case 'cancelled':
    case 'done':
      return null;
    // submitting/action: a BFF round-trip is in flight and the mounted element
    // may be driving inline 3DS2. Keep the host mounted (so handleAction has
    // its DOM node) AND render a busy spinner so the sheet is never blank.
    case 'submitting':
    case 'action':
      return (
        <StyledWrapper mb={0}>
          {cardHost}
          <Spinner />
          <BaseText center testID="adyen-sheet-processing">
            {t('payment.adyen.processing')}
          </BaseText>
        </StyledWrapper>
      );
    // methods/ready: same host placement as submitting/action (first child of
    // StyledWrapper) so React does not remount the div on status transitions.
    default:
      return <StyledWrapper mb={0}>{cardHost}</StyledWrapper>;
  }
};

export default AdyenCardAddSheet;
